import * as T from "three";
import { sway, weathered } from "./art-direction";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { CITY, seeded, type Obstacle } from "./world-data";
import { layout, staticObstacles, PLAZA_BLOCK, PARK_BLOCK, BLOCK_INNER, BUILDINGS, type Placement } from "./layout";
import type { ModelAsset } from "./assets";
import { BUILDING_STYLES, VEHICLE_STYLES, type BuildingStyle, type VehicleStyle } from "./styles";

const rng = seeded(7823),
  r = (a = 0, b = 1) => a + rng() * (b - a);
export const obstacles: Obstacle[] = staticObstacles();
// Simple invisible boxes standing in for buildings, used only for camera
// collision. Raycasting detailed models every frame is far too slow on the CPU.
export const cameraProxies: T.Mesh[] = [];
const proxyMaterial = new T.MeshBasicMaterial();
function proxyBox(parent: T.Object3D, x: number, y: number, z: number, sx: number, sy: number, sz: number) {
  parent.updateWorldMatrix(true, false);
  const m = new T.Mesh(new T.BoxGeometry(), proxyMaterial);
  m.position.set(x, y, z);
  m.scale.set(sx, sy, sz);
  m.updateMatrix();
  m.matrixWorld.multiplyMatrices(parent.matrixWorld, m.matrix);
  m.matrixAutoUpdate = false;
  cameraProxies.push(m);
}
const mats = new Map<string, T.MeshStandardMaterial>();
export function mat(c: string, roughness = 0.9) {
  if (!mats.has(c))
    mats.set(c, weathered(new T.MeshStandardMaterial({ color: c, roughness }), 5, 0.22));
  return mats.get(c)!;
}
export function mesh(g: T.BufferGeometry, m: T.Material, p: T.Object3D, x = 0, y = 0, z = 0) {
  const o = new T.Mesh(g, m);
  o.position.set(x, y, z);
  o.castShadow = true;
  o.receiveShadow = true;
  p.add(o);
  return o;
}
function tiled(url: string, tint = "#ffffff") {
  const material = new T.MeshStandardMaterial({ color: tint, roughness: 0.95 });
  // Skipped in Node tests, which have no image loading.
  if (typeof window !== "undefined") {
    const texture = new T.TextureLoader().load(url);
    texture.wrapS = texture.wrapT = T.RepeatWrapping;
    texture.colorSpace = T.SRGBColorSpace;
    texture.anisotropy = 8;
    material.map = texture;
  }
  return material;
}

export function makeWorld(scene: T.Scene) {
  const root = new T.Group();
  scene.add(root);

  // Base ground under everything (only visible in gaps), then the meeting-square
  // paving and the park lawn, which fill their blocks up to the street pavements.
  const groundSize = (CITY.EDGE + 200) * 2;
  const terrain = mesh(new T.PlaneGeometry(groundSize, groundSize), mat("#4f5048", 1), root, 0, -0.17, 0);
  terrain.rotation.x = -Math.PI / 2;
  terrain.castShadow = false;
  const size = BLOCK_INNER * 2;
  const plazaGeo = new T.PlaneGeometry(size, size);
  plazaGeo.rotateX(-Math.PI / 2);
  const uv = plazaGeo.getAttribute("uv");
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * (size / 4), uv.getY(i) * (size / 4));
  const plaza = mesh(plazaGeo, tiled("assets/city/T_Concrete_BaseColor.jpg", "#d8d2c4"), root, PLAZA_BLOCK.x, 0.01, PLAZA_BLOCK.z);
  plaza.castShadow = false;
  const lawn = mesh(new T.PlaneGeometry(size, size), weathered(new T.MeshStandardMaterial({ color: "#5f8f3d", roughness: 1 }), 0.6, 0.35), root, PARK_BLOCK.x, 0.0, PARK_BLOCK.z);
  lawn.rotation.x = -Math.PI / 2;
  lawn.castShadow = false;

  // A hazy skyline of distant towers beyond the city (no detail, no collision).
  const skyline: T.BufferGeometry[] = [];
  for (let i = 0; i < 90; i++) {
    const a = (i / 90) * Math.PI * 2 + r(-0.02, 0.02), d = r(190, 300);
    const w = r(14, 30), h = r(20, 75);
    const g = new T.BoxGeometry(w, h, r(14, 30));
    g.translate(Math.cos(a) * d, h / 2 - 0.2, Math.sin(a) * d);
    skyline.push(g);
  }
  const towers = new T.Mesh(mergeGeometries(skyline)!, new T.MeshStandardMaterial({ color: "#6f7c88", roughness: 1 }));
  towers.castShadow = false;
  root.add(towers);

  // Collision and camera boxes come straight from the layout; the models arrive
  // later in populate() once they have downloaded.
  for (const p of layout) {
    if (p.box) {
      if (p.kind === "building") {
        const h = p.height ?? 10;
        proxyBox(root, p.box.x, h / 2, p.box.z, p.box.hw * 2 - 0.4, h, p.box.hd * 2 - 0.4);
      }
    }
  }

  // Bake static procedural pieces by material into a handful of meshes.
  root.updateMatrixWorld(true);
  const dynamic = new Set<T.Object3D>([terrain, plaza, lawn, towers]);
  const batches = new Map<T.Material, T.BufferGeometry[]>(),
    originals: T.Mesh[] = [];
  root.traverse((o) => {
    if (!(o instanceof T.Mesh) || o instanceof T.InstancedMesh || Array.isArray(o.material)) return;
    let p: T.Object3D | null = o;
    while (p) {
      if (dynamic.has(p)) return;
      p = p.parent;
    }
    const g = (o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone()).applyMatrix4(o.matrixWorld);
    if (!(o.material as T.MeshStandardMaterial).map) g.deleteAttribute("uv");
    g.deleteAttribute("color");
    g.clearGroups();
    const list = batches.get(o.material) || [];
    list.push(g);
    batches.set(o.material, list);
    originals.push(o);
  });
  for (const o of originals) o.removeFromParent();
  for (const [material, geos] of batches) {
    const merged = mergeGeometries(geos);
    if (merged) {
      const m = mesh(merged, material, root);
      if ((material as T.MeshStandardMaterial).side === T.DoubleSide) m.castShadow = false;
      for (const g of geos) g.dispose();
    }
  }

  // Downloaded models, instanced per model and per map cell. Buildings are the
  // heavy part (~20–45k triangles each), so each building cell also gets a cheap
  // stand-in: plain blocks of the same size, shown instead when the cell is far
  // from the camera. Buildings and parked cars can be re-skinned with another
  // pack (setBuildingStyle / setVehicleStyle) without moving anything.
  const plants: T.InstancedMesh[] = [];
  const cellOf = (x: number, z: number, size: number) => `${Math.floor(x / size)},${Math.floor(z / size)}`;
  const buildingCells = new Map<string, { centre: T.Vector3; detailed: T.InstancedMesh[]; standIn: T.InstancedMesh }>();
  const standInMaterial = new T.MeshStandardMaterial({ color: "#ffffff", roughness: 1 });
  const standInColours = ["#7b4a3c", "#8a5a46", "#6c4136", "#9b8f78", "#5d5b58"].map((c) => new T.Color(c));
  const q = new T.Quaternion(), up = new T.Vector3(0, 1, 0), at = new T.Vector3(), sv = new T.Vector3();
  function instanced(asset: ModelAsset, matrices: T.Matrix4[], shadows = true) {
    const inst = new T.InstancedMesh(asset.geometry, asset.materials, matrices.length);
    matrices.forEach((m, i) => inst.setMatrixAt(i, m));
    inst.castShadow = shadows;
    inst.receiveShadow = true;
    inst.computeBoundingSphere();
    root.add(inst);
    return inst;
  }
  const buildings = layout.filter((p) => p.kind === "building");
  const parked = layout.filter((p) => p.kind === "car");

  function populate(models: Map<string, ModelAsset>) {
    const groups = new Map<string, Placement[]>();
    for (const p of layout) {
      if (p.kind === "building" || p.kind === "car") continue;
      const key = `${p.model}|${cellOf(p.x, p.z, p.kind === "plant" ? 40 : 120)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    }
    const swayed = new Map<string, ModelAsset>();
    for (const [key, list] of groups) {
      const name = key.split("|")[0];
      let asset = models.get(name);
      if (!asset) continue;
      const kind = list[0].kind;
      if (kind === "plant" || kind === "tree") {
        const amount = kind === "tree" ? 0.035 : 0.16;
        const swayKey = `${name}:${amount}`;
        let bent = swayed.get(swayKey);
        if (!bent) {
          bent = { geometry: asset.geometry, materials: asset.materials.map((m) => sway(m.clone(), amount)) };
          swayed.set(swayKey, bent);
        }
        asset = bent;
      }
      const matrices = list.map((p) => {
        // Street trees stand in planters, so they start at the planter's soil.
        const y = p.kind === "tree" && isInPlanter(p) ? (p.scale < 0.75 ? 0.42 : 0.6) : 0;
        return new T.Matrix4().compose(at.set(p.x, y, p.z), q.setFromAxisAngle(up, p.rot), sv.setScalar(p.scale));
      });
      const inst = instanced(asset, matrices, list[0].kind !== "street" && list[0].kind !== "plant");
      if (list[0].kind === "plant") plants.push(inst);
    }
    // Stand-in blocks for distant buildings (style-independent).
    const unit = new T.BoxGeometry();
    unit.translate(0, 0.5, 0);
    const byCell = new Map<string, Placement[]>();
    for (const p of buildings) {
      const k = cellOf(p.x, p.z, 60);
      if (!byCell.has(k)) byCell.set(k, []);
      byCell.get(k)!.push(p);
    }
    for (const [k, list] of byCell) {
      const standIn = new T.InstancedMesh(unit, standInMaterial, list.length);
      const centre = new T.Vector3();
      list.forEach((p, i) => {
        const b = p.box!, h = BUILDINGS[p.model as keyof typeof BUILDINGS].h;
        standIn.setMatrixAt(i, new T.Matrix4().compose(at.set(b.x, 0, b.z), q.identity(), sv.set(b.hw * 2 - 0.6, h, b.hd * 2 - 0.6)));
        standIn.setColorAt(i, standInColours[i % standInColours.length]);
        centre.add(at.set(p.x, 0, p.z));
      });
      centre.divideScalar(list.length);
      standIn.castShadow = standIn.receiveShadow = true;
      standIn.visible = false;
      root.add(standIn);
      buildingCells.set(k, { centre, detailed: [], standIn });
    }
  }

  // Buildings in a chosen style. The Quaternius models fill their slots exactly.
  // Other packs have small shop-sized buildings, so each slot gets a row of them
  // side by side at a natural scale (stretched slightly to fit the slot exactly).
  function setBuildingStyle(style: BuildingStyle, models: Map<string, ModelAsset>) {
    for (const cell of buildingCells.values()) {
      for (const m of cell.detailed) root.remove(m);
      cell.detailed = [];
    }
    const pool = BUILDING_STYLES[style].models;
    const natural = style === "kaykit" ? 5 : 6; // metres per model unit
    const sizeOf = (name: string) => {
      const g = models.get(name)!.geometry;
      if (!g.boundingBox) g.computeBoundingBox();
      return g.boundingBox!.getSize(new T.Vector3());
    };
    const groups = new Map<string, { cell: string; matrices: T.Matrix4[] }>();
    const put = (model: string, cell: string, matrix: T.Matrix4) => {
      const key = `${model}|${cell}`;
      if (!groups.has(key)) groups.set(key, { cell, matrices: [] });
      groups.get(key)!.matrices.push(matrix);
    };
    buildings.forEach((p, index) => {
      const cell = cellOf(p.x, p.z, 60);
      q.setFromAxisAngle(up, p.rot + BUILDING_STYLES[style].turn);
      if (!pool.length) return put(p.model, cell, new T.Matrix4().compose(at.set(p.x, 0, p.z), q, sv.setScalar(1)));
      const slot = BUILDINGS[p.model as keyof typeof BUILDINGS];
      const width = slot.maxX - slot.minX, depth = slot.maxZ - slot.minZ - 0.3;
      const parts: { name: string; size: T.Vector3 }[] = [];
      let total = 0;
      for (let k = 0; total < width * 0.8 && k < 6; k++) {
        const name = pool[(index * 7 + k * 3 + 1) % pool.length];
        if (!models.has(name)) continue;
        const size = sizeOf(name);
        parts.push({ name, size });
        total += size.x * natural;
      }
      if (!parts.length) return;
      const fit = width / total, c = Math.cos(p.rot), sn = Math.sin(p.rot), b = p.box!;
      let cursor = -width / 2;
      for (const part of parts) {
        const w = part.size.x * natural * fit, lx = cursor + w / 2;
        cursor += w;
        // local +x maps to world (cos r, -sin r)
        const m = new T.Matrix4().compose(at.set(b.x + lx * c, 0, b.z - lx * sn), q, sv.set(natural * fit, natural, depth / part.size.z));
        put(part.name, cell, m);
      }
    });
    for (const [key, g] of groups) {
      const asset = models.get(key.split("|")[0]);
      if (!asset) continue;
      buildingCells.get(g.cell)?.detailed.push(instanced(asset, g.matrices));
    }
  }

  // Parked cars in a chosen vehicle style. Each parked car remembers which
  // instance draws it, so it can be taken for a drive and parked elsewhere.
  let parkedMeshes: T.InstancedMesh[] = [];
  let parkedSlots: { mesh: T.InstancedMesh; index: number; model: string }[] = [];
  let vehicleStyle: VehicleStyle = "kenney";
  let vehicleModels = new Map<string, ModelAsset>();
  const hidden = new T.Matrix4().makeScale(0, 0, 0);
  function parkedMatrix(i: number) {
    const p = parked[i], v = VEHICLE_STYLES[vehicleStyle];
    return new T.Matrix4().compose(at.set(p.x, -0.15, p.z), q.setFromAxisAngle(up, p.rot + v.turn), sv.setScalar(v.scale));
  }
  function setVehicleStyle(style: VehicleStyle, models: Map<string, ModelAsset>) {
    for (const m of parkedMeshes) root.remove(m);
    parkedMeshes = [];
    parkedSlots = [];
    vehicleStyle = style;
    vehicleModels = models;
    const v = VEHICLE_STYLES[style];
    const byModel = new Map<string, number[]>();
    parked.forEach((_, index) => {
      const model = v.models[(index * 5 + 1) % v.models.length];
      if (!byModel.has(model)) byModel.set(model, []);
      byModel.get(model)!.push(index);
    });
    for (const [model, ids] of byModel) {
      const asset = models.get(model);
      if (!asset) continue;
      const mesh = instanced(asset, ids.map((i) => (driving.has(i) ? hidden : parkedMatrix(i))));
      ids.forEach((carIndex, i) => (parkedSlots[carIndex] = { mesh, index: i, model }));
      parkedMeshes.push(mesh);
    }
  }
  // Parked cars currently being driven (hidden from the parked set).
  const driving = new Set<number>();
  const vehicles = {
    list: parked,
    /** Model and drawing details of parked car i in the current style. */
    asset(i: number) {
      const slot = parkedSlots[i], v = VEHICLE_STYLES[vehicleStyle];
      return slot ? { asset: vehicleModels.get(slot.model)!, scale: v.scale, turn: v.turn } : null;
    },
    take(i: number) {
      driving.add(i);
      const slot = parkedSlots[i];
      if (slot) {
        slot.mesh.setMatrixAt(slot.index, hidden);
        slot.mesh.instanceMatrix.needsUpdate = true;
      }
      parked[i].box!.x = 1e6; // no longer an obstacle while driven
    },
    park(i: number, x: number, z: number, rot: number) {
      driving.delete(i);
      const p = parked[i];
      p.x = x;
      p.z = z;
      p.rot = rot;
      // Collision box: a square that covers the car at any angle.
      Object.assign(p.box!, { x, z, hw: 1.7, hd: 1.7 });
      const slot = parkedSlots[i];
      if (slot) {
        slot.mesh.setMatrixAt(slot.index, parkedMatrix(i));
        slot.mesh.instanceMatrix.needsUpdate = true;
        slot.mesh.computeBoundingSphere();
      }
    },
  };

  // Detail switches by distance (shorter on Low graphics); Low also hides the
  // small park plants.
  let detail = "high";
  function update(eye: T.Vector3) {
    const near = detail === "low" ? 85 : 170;
    for (const c of buildingCells.values()) {
      const show = Math.hypot(c.centre.x - eye.x, c.centre.z - eye.z) < near;
      for (const m of c.detailed) m.visible = show;
      c.standIn.visible = !show;
    }
    for (const p of plants) p.visible = detail !== "low";
  }
  function setQuality(quality: string) {
    detail = quality;
  }
  return { root, terrain, setQuality, update, populate, setBuildingStyle, setVehicleStyle, vehicles };
}
const planters = layout.filter((p) => p.model === "city/Prop_Planter_Single");
function isInPlanter(p: Placement) {
  return planters.some((q) => Math.abs(q.x - p.x) < 0.01 && Math.abs(q.z - p.z) < 0.01);
}
